import APIClient from '../../../lib/api';

class ClientService {
  async getClients(params = {}) {
    return this.getAll(params);
  }

  async getAll(params = {}) {
    const defaultParams = {
      skip: 0,
      limit: 50,
    };
    
    const queryParams = { ...defaultParams, ...params };
    
    try {
      const response = await APIClient.clients.getAll(queryParams);
      return {
        success: true,
        data: response.data.data || response.data,
        total: response.data.total || response.data.length
      };
    } catch (error) {
      console.error('ClientService.getAll Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης πελατών'
      };
    }
  }

  async getById(clientId) {
    try {
      const response = await APIClient.clients.getById(clientId);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('ClientService.getById Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης πελάτη'
      };
    }
  }

  async create(clientData) {
    try {
      const response = await APIClient.clients.create(clientData);
      return {
        success: true,
        data: response.data,
        message: 'Ο πελάτης δημιουργήθηκε επιτυχώς'
      };
    } catch (error) {
      console.error('ClientService.create Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα δημιουργίας πελάτη'
      };
    }
  }

  async update(clientId, updateData) {
    try {
      const response = await APIClient.clients.update(clientId, updateData);
      return {
        success: true,
        data: response.data,
        message: 'Ο πελάτης ενημερώθηκε επιτυχώς'
      };
    } catch (error) {
      console.error('ClientService.update Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα ενημέρωσης πελάτη'
      };
    }
  }

  async delete(clientId) {
    try {
      await APIClient.clients.delete(clientId);
      return {
        success: true,
        message: 'Ο πελάτης διαγράφηκε επιτυχώς'
      };
    } catch (error) {
      console.error('ClientService.delete Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα διαγραφής πελάτη'
      };
    }
  }

  async deactivate(clientId) {
    try {
      const response = await APIClient.clients.update(clientId, { is_active: false });
      return {
        success: true,
        data: response.data,
        message: 'Ο πελάτης απενεργοποιήθηκε επιτυχώς'
      };
    } catch (error) {
      console.error('ClientService.deactivate Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα απενεργοποίησης πελάτη'
      };
    }
  }

  async hardDelete(clientId) {
    try {
      await APIClient.clients.hardDelete(clientId);
      return {
        success: true,
        message: 'Ο πελάτης και όλα τα σχετικά αρχεία διαγράφηκαν επιτυχώς'
      };
    } catch (error) {
      console.error('ClientService.hardDelete Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα διαγραφής πελάτη'
      };
    }
  }

  async getStatistics(params = {}) {
    try {
      const response = await APIClient.clients.getStatistics(params);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('ClientService.getStatistics Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης στατιστικών πελατών'
      };
    }
  }

  async getPublicToken(clientId) {
    try {
      const response = await APIClient.clients.getPublicToken(clientId);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('ClientService.getPublicToken Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα λήψης public token'
      };
    }
  }

  // Billing Info Methods
  async getBillingInfo(clientId) {
    try {
      const response = await APIClient.clients.getBillingInfo(clientId);
      return {
        success: true,
        data: response.data || []
      };
    } catch (error) {
      console.error('ClientService.getBillingInfo Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα φόρτωσης billing info'
      };
    }
  }

  async createBillingInfo(clientId, billingInfoData) {
    try {
      const response = await APIClient.clients.createBillingInfo(clientId, billingInfoData);
      return {
        success: true,
        data: response.data,
        message: 'Τα φορολογικά στοιχεία δημιουργήθηκαν επιτυχώς'
      };
    } catch (error) {
      console.error('ClientService.createBillingInfo Error:', error);
      // Keep the original error structure for proper handling
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα δημιουργίας billing info',
        errorResponse: error.response?.data // Keep full error response for field-level errors
      };
    }
  }

  async updateBillingInfo(billingInfoId, updateData) {
    try {
      const response = await APIClient.clients.updateBillingInfo(billingInfoId, updateData);
      return {
        success: true,
        data: response.data,
        message: 'Τα φορολογικά στοιχεία ενημερώθηκαν επιτυχώς'
      };
    } catch (error) {
      console.error('ClientService.updateBillingInfo Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα ενημέρωσης billing info'
      };
    }
  }

  async deleteBillingInfo(billingInfoId) {
    try {
      await APIClient.clients.deleteBillingInfo(billingInfoId);
      return {
        success: true,
        message: 'Τα φορολογικά στοιχεία διαγράφηκαν επιτυχώς'
      };
    } catch (error) {
      console.error('ClientService.deleteBillingInfo Error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Σφάλμα διαγραφής billing info'
      };
    }
  }

  validate(clientData) {
    const errors = {};

    if (!clientData.name || clientData.name.trim() === '') {
      errors.name = 'Το όνομα είναι υποχρεωτικό';
    }

    if (!clientData.email || clientData.email.trim() === '') {
      errors.email = 'Το email είναι υποχρεωτικό';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.email)) {
      errors.email = 'Μη έγκυρο email';
    }

    if (clientData.phone && !/^[0-9+\-\s()]+$/.test(clientData.phone)) {
      errors.phone = 'Μη έγκυρος αριθμός τηλεφώνου';
    }

    // VAT validation moved to billing_info

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

const clientService = new ClientService();
export default clientService;